// frontend/src/pages/SalesActionPlan.js
// Monthly Action Plan grid for a Sales EMC: 4 rows (Targets / Pipeline /
// Activity / Support) x 3 month columns, for a given cycle.
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getActionPlans, updateActionPlan, getActionPlansTeam,
  getCycles, getUsers,
} from '../services/api';
import { Card, Avatar, Badge, Btn, Select, Spinner, SectionTitle } from '../components/UI';
import PageHeader from '../components/PageHeader';

const ROWS = [
  { key: 'targets',              label: 'Targets',                     placeholder: 'e.g. Acquisition Revenue $100,000 · Renew 90%' },
  { key: 'pipeline_management',  label: 'Pipeline management',         placeholder: 'What needs to happen to my pipe to reach target?' },
  { key: 'activity_commitment',  label: 'Monthly activity commitment', placeholder: 'I will meet with…\nI will call 50 businesses in…\nI will research …\nI will visit …\nI will build a campaign …' },
  { key: 'support',              label: 'Support',                     placeholder: 'What support do I need? Is this level of support feasible?' },
];

function AutoGrowTextarea({ value, onChange, placeholder, readOnly }) {
  const empty = !value;
  return (
    <textarea
      value={value || ''}
      onChange={e => onChange && onChange(e.target.value)}
      placeholder={placeholder}
      readOnly={readOnly}
      style={{
        width: '100%',
        minHeight: 140,
        padding: '10px 12px',
        border: readOnly
          ? '0.5px dashed var(--border-md)'
          : (empty ? '1px dashed var(--navy-500)' : '1px solid var(--border-md)'),
        borderRadius: 'var(--radius-md)',
        background: readOnly ? 'var(--gray-50)' : (empty ? 'var(--navy-50)' : 'var(--surface)'),
        color: 'var(--text-1)',
        fontSize: 13, fontFamily: 'inherit', lineHeight: 1.5,
        resize: 'vertical',
        outline: 'none',
        whiteSpace: 'pre-wrap',
        cursor: readOnly ? 'default' : 'text',
        transition: 'border-color 0.15s, background 0.15s',
      }}
      onFocus={e => { if (!readOnly) e.target.style.borderColor = 'var(--navy-600)'; }}
      onBlur={e => { e.target.style.borderColor = empty ? 'var(--navy-500)' : 'var(--border-md)'; }}
    />
  );
}

export default function SalesActionPlan() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [cycles,    setCycles]    = useState([]);
  const [users,     setUsers]     = useState([]);
  const [teamRows,  setTeamRows]  = useState([]);
  const [months,    setMonths]    = useState([]);  // 3 rows for the currently-focused cycle
  const [allPlans,  setAllPlans]  = useState({});  // { [cycleId]: [monthRow,…] } for every cycle
  const [dirty,     setDirty]     = useState({});  // id -> { field: value }
  const [saving,    setSaving]    = useState(false);
  const [loading,   setLoading]   = useState(true);
  const [loadError, setLoadError] = useState(null); // backend message if the load failed

  const isManager  = ['admin','manager'].includes(user?.role);
  const cycleId    = searchParams.get('cycle')    || '';
  const employeeId = searchParams.get('employee') || String(user?.id || '');

  const setParam = (k, v) => {
    const next = new URLSearchParams(searchParams);
    if (v === '' || v == null) next.delete(k); else next.set(k, v);
    setSearchParams(next, { replace: true });
  };

  // Initial load — cycles + users (for manager picker)
  useEffect(() => {
    Promise.all([
      getCycles({ program_id: 2 }).catch(() => []),
      isManager ? getUsers().catch(() => []) : Promise.resolve([]),
    ]).then(([c, u]) => {
      const cyc = Array.isArray(c) ? c : [];
      setCycles(cyc);
      setUsers(Array.isArray(u) ? u : []);
      if (!cycleId) {
        const active = cyc.find(x => x.status === 'active');
        if (active) setParam('cycle', String(active.id));
      }
    }).finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);

  // Load the plan for the currently-focused cycle
  useEffect(() => {
    if (!cycleId || !employeeId) { setMonths([]); setLoadError(null); return; }
    setLoadError(null);
    getActionPlans({ cycle_id: cycleId, employee_id: employeeId })
      .then(d => {
        const list = Array.isArray(d) ? d : [];
        setMonths(list);
        setAllPlans(prev => ({ ...prev, [cycleId]: list }));
        setDirty({});
      })
      .catch(err => {
        setMonths([]);
        setLoadError(err.response?.data?.error || err.message || 'Could not load the action plan');
      });
  }, [cycleId, employeeId]);

  // Year view: fetch plans for every cycle (this employee) in parallel so the
  // strip can show completion % for each quarter without navigating.
  useEffect(() => {
    if (!employeeId || cycles.length === 0) return;
    let cancelled = false;
    Promise.all(
      cycles.map(c =>
        getActionPlans({ cycle_id: c.id, employee_id: employeeId })
          .then(d => [c.id, Array.isArray(d) ? d : []])
          .catch(() => [c.id, []])
      )
    ).then(entries => {
      if (cancelled) return;
      const next = {};
      entries.forEach(([k, v]) => { next[k] = v; });
      setAllPlans(next);
    });
    return () => { cancelled = true; };
  }, [employeeId, cycles]);

  // Load team summary (for managers) to show a completion badge
  useEffect(() => {
    if (!isManager || !cycleId) { setTeamRows([]); return; }
    getActionPlansTeam(cycleId).then(d => setTeamRows(Array.isArray(d) ? d : [])).catch(() => setTeamRows([]));
  }, [isManager, cycleId]);

  const onEdit = (id, field, value) => {
    setDirty(d => ({ ...d, [id]: { ...d[id], [field]: value } }));
  };

  const save = async () => {
    const ids = Object.keys(dirty);
    if (ids.length === 0) return;
    setSaving(true);
    try {
      await Promise.all(ids.map(id => updateActionPlan(id, dirty[id])));
      // Re-fetch to sync with server-trimmed values
      const d = await getActionPlans({ cycle_id: cycleId, employee_id: employeeId });
      setMonths(Array.isArray(d) ? d : []);
      setDirty({});
    } catch (e) {
      alert(e.response?.data?.error || 'Error saving action plan');
    } finally {
      setSaving(false);
    }
  };

  const currentValue = (month, field) => {
    const d = dirty[month.id] && dirty[month.id][field];
    return d !== undefined ? d : (month[field] || '');
  };

  const cycle = useMemo(() => cycles.find(c => String(c.id) === String(cycleId)), [cycles, cycleId]);

  // Editability: managers/admins can edit any plan they have access to (the
  // backend enforces subtree via can_access_user). Employees can only edit
  // their own — and the employee picker is hidden for them, so they can only
  // ever land on their own plan anyway.
  const viewingSelf = String(employeeId) === String(user?.id || '');
  const readOnly    = !isManager && !viewingSelf;
  const targetUser  = users.find(u => String(u.id) === String(employeeId));
  const targetName  = viewingSelf ? 'You' : (targetUser?.name || `Employee #${employeeId}`);
  const targetInitials = viewingSelf ? (user?.avatar_initials || '?') : (targetUser?.avatar_initials || '?');

  // Direct reports (for the My team sidebar)
  const directReports = useMemo(() => users.filter(u =>
    String(u.manager_id) === String(user?.id || '') && u.id !== user?.id
  ), [users, user]);

  // ── Progress helpers ─────────────────────────────────────────────────
  // A month is "complete" when all 4 rows have non-empty text.
  const isFilled = (val) => typeof val === 'string' && val.trim().length > 0;
  const monthFilledCount = (m, fallbackMonths) => {
    // When called for the currently-focused month, prefer dirty-aware value
    const from = fallbackMonths ? m : m;
    return ROWS.filter(r => {
      const dv = dirty[from.id] && dirty[from.id][r.key];
      const val = dv !== undefined ? dv : from[r.key];
      return isFilled(val);
    }).length;
  };
  const planProgress = (cycleId) => {
    const monthsForCycle = allPlans[cycleId] || [];
    const total = 3 * ROWS.length; // 12 cells
    const filled = monthsForCycle.reduce(
      (s, m) => s + ROWS.filter(r => isFilled(m[r.key])).length,
      0
    );
    return { filled, total, pct: total ? Math.round((filled / total) * 100) : 0 };
  };
  const currentCycleProgress = (() => {
    const total = months.length * ROWS.length;
    const filled = months.reduce((s, m) => s + monthFilledCount(m), 0);
    return { filled, total, pct: total ? Math.round((filled / total) * 100) : 0 };
  })();
  const progressColor = pct => pct >= 75 ? 'var(--navy-500)' : pct >= 25 ? 'var(--amber-500)' : 'var(--gray-200)';
  const statusBadgeColor = status => status === 'active' ? 'green' : status === 'closed' ? 'gray' : 'amber';

  if (loading) return <Spinner />;

  return (
    <div style={{ padding: '20px clamp(16px, 3vw, 32px)', maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ marginBottom: 14 }}>
        <PageHeader moduleKey="sales-action-plans" helpTask="sales-action-plan"
          subtitle="Document what activity, and what level of activity, drives positive sales results — monthly." />
      </div>

      {/* Who's plan? — context banner so it's always clear */}
      <Card style={{ marginBottom: 14, borderLeft: `4px solid ${viewingSelf ? 'var(--navy-600)' : 'var(--blue-500)'}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Avatar initials={targetInitials} size={40} />
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {viewingSelf ? 'Your plan' : 'Viewing a team member'}
            </div>
            <div style={{ fontSize: 15, fontWeight: 500, marginTop: 2 }}>{targetName}</div>
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>
              {readOnly ? 'Read-only' : 'You can edit any cell below'}
            </div>
          </div>
          {!viewingSelf && (
            <Btn variant="secondary" size="sm" onClick={() => setParam('employee', String(user?.id || ''))}>← Back to my plan</Btn>
          )}
        </div>
      </Card>

      {/* Your team — direct reports shortcut for managers */}
      {isManager && directReports.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <SectionTitle>Your team · click a report to open their plan</SectionTitle>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: 10, marginTop: 10,
          }}>
            <div
              onClick={() => setParam('employee', String(user?.id || ''))}
              style={{
                padding: '12px 14px', cursor: 'pointer',
                border: viewingSelf ? '1px solid var(--navy-500)' : '0.5px solid var(--border)',
                borderRadius: 10, background: viewingSelf ? 'var(--navy-50)' : 'var(--surface)',
                boxShadow: viewingSelf ? 'var(--shadow-sm)' : 'none',
                transition: 'all 0.12s',
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                <Avatar initials={user?.avatar_initials} size={28} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 500 }}>You</div>
                  <div style={{ fontSize: 10, color: 'var(--text-3)' }}>{user?.job_title || 'Manager'}</div>
                </div>
                {viewingSelf && <Badge color="green">Open</Badge>}
              </div>
            </div>
            {directReports.map(r => {
              const isOpen = String(r.id) === String(employeeId);
              return (
                <div key={r.id}
                     onClick={() => setParam('employee', String(r.id))}
                     style={{
                       padding: '12px 14px', cursor: 'pointer',
                       border: isOpen ? '1px solid var(--blue-500)' : '0.5px solid var(--border)',
                       borderRadius: 10, background: isOpen ? 'var(--blue-50)' : 'var(--surface)',
                       boxShadow: isOpen ? 'var(--shadow-sm)' : 'none',
                       transition: 'all 0.12s',
                     }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                    <Avatar initials={r.avatar_initials} size={28} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</div>
                      <div style={{ fontSize: 10, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.job_title || r.role_type || '—'}</div>
                    </div>
                    {isOpen && <Badge color="blue">Open</Badge>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* First-run CTA — shown only when this is your own plan and nothing is
          filled yet. Clicking the CTA focuses the first empty cell. */}
      {!readOnly && currentCycleProgress.filled === 0 && months.length > 0 && (
        <Card style={{ background: 'var(--navy-50)', border: '0.5px solid var(--navy-100)', marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ fontSize: 22 }}>✍️</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--navy-600)' }}>Start writing your action plan</div>
              <div style={{ fontSize: 12, color: 'var(--navy-600)', marginTop: 4, lineHeight: 1.5 }}>
                Each row below is a section (Targets, Pipeline, Activity, Support) × 3 months.
                Click any cell to type. Progress saves when you hit <strong>Save changes</strong>.
              </div>
            </div>
            <Btn size="sm" onClick={() => {
              const firstEmpty = document.querySelector('textarea:not([readonly])');
              if (firstEmpty) { firstEmpty.focus(); firstEmpty.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
            }}>Jump to first cell</Btn>
          </div>
        </Card>
      )}

      {/* Year at a glance — all cycles with progress */}
      {cycles.length > 0 && (
        <div style={{ marginBottom: 22 }}>
          <SectionTitle>Year at a glance</SectionTitle>
          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${Math.max(cycles.length, 1)}, minmax(180px, 1fr))`,
            gap: 10, marginTop: 10,
            overflowX: 'auto',
          }}>
            {cycles.map(c => {
              const prog = planProgress(c.id);
              const isActive = String(c.id) === String(cycleId);
              return (
                <div key={c.id}
                     onClick={() => setParam('cycle', c.id)}
                     style={{
                       padding: '14px 16px',
                       background: isActive ? 'var(--navy-50)' : 'var(--surface)',
                       border: isActive ? '1px solid var(--navy-500)' : '0.5px solid var(--border)',
                       borderRadius: 10, cursor: 'pointer',
                       boxShadow: isActive ? 'var(--shadow-sm)' : 'none',
                       transition: 'all 0.12s',
                       display: 'flex', flexDirection: 'column', gap: 8,
                     }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.name}
                    </div>
                    <Badge color={statusBadgeColor(c.status)}>{c.status}</Badge>
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text-3)' }}>
                    {c.start_date} → {c.end_date}
                  </div>
                  <div style={{ height: 6, background: 'var(--gray-100)', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{
                      width: `${prog.pct}%`, height: '100%',
                      background: progressColor(prog.pct),
                      transition: 'width 0.3s',
                    }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 11 }}>
                    <span style={{ color: 'var(--text-3)' }}>{prog.filled}/{prog.total} fields</span>
                    <span style={{ fontSize: 16, fontWeight: 500, color: progressColor(prog.pct) }}>{prog.pct}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Controls */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 18, flexWrap: 'wrap' }}>
        <div>
          <label style={{ display: 'block', fontSize: 11, color: 'var(--text-3)', marginBottom: 4 }}>Cycle</label>
          <Select value={cycleId} onChange={e => setParam('cycle', e.target.value)} style={{ width: 260 }}>
            <option value="">Select cycle…</option>
            {cycles.map(c => <option key={c.id} value={c.id}>{c.name}{c.status === 'active' ? ' · Active' : ''}</option>)}
          </Select>
        </div>
        {isManager && (
          <div>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--text-3)', marginBottom: 4 }}>Employee</label>
            <Select value={employeeId} onChange={e => setParam('employee', e.target.value)} style={{ width: 260 }}>
              <option value={String(user?.id || '')}>Me</option>
              {users
                .filter(u => Number(u.program_id) === 2)
                .map(u => <option key={u.id} value={u.id}>{u.name}{u.job_title ? ` — ${u.job_title}` : ''}</option>)}
            </Select>
          </div>
        )}
        <div style={{ marginLeft: 'auto' }}>
          {!readOnly && (
            <Btn onClick={save} disabled={saving || Object.keys(dirty).length === 0}>
              {saving ? 'Saving…' : Object.keys(dirty).length > 0 ? `Save changes (${Object.keys(dirty).length})` : 'Saved'}
            </Btn>
          )}
        </div>
      </div>

      {cycle && (
        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Badge color={statusBadgeColor(cycle.status)}>{cycle.name}</Badge>
            <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{cycle.start_date} → {cycle.end_date}</span>
            <div style={{ flex: 1, minWidth: 200, display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ flex: 1, height: 8, background: 'var(--gray-100)', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{
                  width: `${currentCycleProgress.pct}%`, height: '100%',
                  background: progressColor(currentCycleProgress.pct),
                  transition: 'width 0.3s',
                }} />
              </div>
              <div style={{ fontSize: 12, color: progressColor(currentCycleProgress.pct), fontWeight: 500, minWidth: 110, textAlign: 'right' }}>
                {currentCycleProgress.filled}/{currentCycleProgress.total} · {currentCycleProgress.pct}%
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Grid */}
      {months.length === 0 ? (
        <Card style={{ borderLeft: loadError ? '4px solid var(--red-500)' : '0.5px solid var(--border)' }}>
          <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-3)', fontSize: 13 }}>
            {!cycleId && 'Pick a cycle above to load the plan.'}
            {cycleId && loadError && (
              <div>
                <div style={{ color: 'var(--red-700)', fontWeight: 500, marginBottom: 6 }}>Couldn't load the plan</div>
                <div style={{ fontSize: 12 }}>{loadError}</div>
                <div style={{ fontSize: 11, marginTop: 10, color: 'var(--text-3)' }}>
                  If this cycle belongs to the Sales program and the issue persists, let the engineering team know.
                </div>
              </div>
            )}
            {cycleId && !loadError && (
              <div>
                <div style={{ marginBottom: 8 }}>Creating your plan…</div>
                <Btn size="sm" onClick={() => {
                  getActionPlans({ cycle_id: cycleId, employee_id: employeeId })
                    .then(d => setMonths(Array.isArray(d) ? d : []))
                    .catch(err => setLoadError(err.response?.data?.error || 'Retry failed'));
                }}>Retry</Btn>
              </div>
            )}
          </div>
        </Card>
      ) : (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: `200px repeat(${months.length}, 1fr)`, borderBottom: '0.5px solid var(--border)' }}>
            <div style={{ padding: '12px 14px', background: 'var(--gray-50)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-3)', fontWeight: 500 }}>
              Row
            </div>
            {months.map(m => {
              const filled = monthFilledCount(m);
              const pct    = ROWS.length ? Math.round((filled / ROWS.length) * 100) : 0;
              return (
                <div key={m.id} style={{ padding: '10px 14px', background: 'var(--gray-50)', borderLeft: '0.5px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
                    <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-2)', fontWeight: 500 }}>
                      {m.month_label}
                    </div>
                    <Badge color={filled === ROWS.length ? 'green' : filled === 0 ? 'gray' : 'amber'}>
                      {filled}/{ROWS.length}
                    </Badge>
                  </div>
                  <div style={{ height: 4, background: 'var(--gray-100)', borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{
                      width: `${pct}%`, height: '100%',
                      background: progressColor(pct),
                      transition: 'width 0.3s',
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
          {ROWS.map(row => (
            <div key={row.key} style={{ display: 'grid', gridTemplateColumns: `200px repeat(${months.length}, 1fr)`, borderBottom: '0.5px solid var(--border)' }}>
              <div style={{ padding: '14px', fontSize: 13, fontWeight: 500, background: 'var(--gray-50)', borderRight: '0.5px solid var(--border)' }}>
                {row.label}
              </div>
              {months.map(m => (
                <div key={m.id} style={{ padding: '12px', borderLeft: '0.5px solid var(--border)' }}>
                  <AutoGrowTextarea
                    value={currentValue(m, row.key)}
                    onChange={v => onEdit(m.id, row.key, v)}
                    placeholder={row.placeholder}
                    readOnly={readOnly}
                  />
                </div>
              ))}
            </div>
          ))}
        </Card>
      )}

      {/* Manager team summary */}
      {isManager && teamRows.length > 0 && (
        <div style={{ marginTop: 28 }}>
          <SectionTitle>Team completion for this cycle</SectionTitle>
          <Card style={{ padding: 0, overflow: 'hidden', marginTop: 10 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)' }}>
                  {['Employee','Role','Months filled','Action'].map(h => (
                    <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-3)', borderBottom: '0.5px solid var(--border)', fontWeight: 500 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {teamRows.map(r => (
                  <tr key={r.employee_id} style={{ borderBottom: '0.5px solid var(--border)' }}>
                    <td style={{ padding: '10px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Avatar initials={r.avatar_initials} size={26} />
                        <span style={{ fontWeight: 500 }}>{r.name}</span>
                      </div>
                    </td>
                    <td style={{ padding: '10px 14px', color: 'var(--text-3)' }}>{r.job_title || '—'}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <Badge color={Number(r.months_filled) === 3 ? 'green' : Number(r.months_filled) === 0 ? 'red' : 'amber'}>
                        {r.months_filled}/3
                      </Badge>
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <Btn variant="secondary" size="sm" onClick={() => setParam('employee', String(r.employee_id))}>View plan</Btn>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}
    </div>
  );
}
